import api from 'api/api'
import useStore from 'state/knovStore'
import toast from 'utils/toast'
import { confirmAsync } from 'components/shared/confirmUtils'
import { openCenter } from 'components/filters/useStreamFilters'
import questModel from 'components/quests/questModel'
import { pickMediaFile } from 'components/quests/mintMediaThreadFlow'
import { createMintMediaThreadRunner } from 'components/quests/mintMediaThreadPolicy'
import { v4 as uuid } from 'uuid'
import {
    MUSIC_TITLE_MAX_LENGTH,
    type MusicForm,
    type MusicKind,
} from 'components/modals/NewMusicModal'

const MUSIC_ACCEPT = 'audio/*,video/*'
const FLOW_TOAST_KIND = 'new-music'

function musicKindForFile(file: File): MusicKind | null {
    const type = file.type.toLowerCase()
    if (type.startsWith('audio/')) return 'audio'
    if (type.startsWith('video/')) return 'video'
    return null
}

function titleFromFilename(name: string): string {
    return name
        .replace(/\.[^.]+$/, '')
        .replace(/[_-]+/g, ' ')
        .trim()
        .slice(0, MUSIC_TITLE_MAX_LENGTH)
}

function openNewMusicForm(file: File, kind: MusicKind): Promise<MusicForm | null> {
    const initial: MusicForm = { title: titleFromFilename(file.name), description: '', cover: null }

    return new Promise(resolve => {
        let settled = false
        const settle = (form: MusicForm | null) => {
            if (settled) return
            settled = true
            resolve(form)
        }
        useStore.getState().actions.modalActions.openNewMusicModal({
            file,
            kind,
            initial,
            onSubmit: form => settle(form),
            onCancel: () => settle(null),
        })
    })
}

/**
 * The music channel's New Music: pick an audio or video file, fill in its
 * title, description and (for audio) cover, and it posts as a public music
 * thread (thread_type music) whose first post carries the song.
 */
async function performNewMusicFlow() {
    if (!window.gon?.currentUser) {
        useStore.getState().actions.modalActions.openAuthModal('signup', 'new_music')
        return
    }

    const file = await pickMediaFile(MUSIC_ACCEPT)
    if (!file) return

    const kind = musicKindForFile(file)
    if (!kind || file.size <= 0) {
        await confirmAsync(
            kind
                ? 'The selected file is empty. Choose another song.'
                : 'Choose an audio or video file.',
            { confirmLabel: 'OK', hideCancel: true, iconType: 'error' },
        )
        return
    }

    const form = await openNewMusicForm(file, kind)
    if (!form) return

    const title = form.title.trim() || titleFromFilename(file.name)
    const description = form.description.trim()

    toast.pending('Posting song', { kind: FLOW_TOAST_KIND, detail: 'Uploading…' })

    let signedId: string | undefined
    try {
        const media = await api.preuploadAnswerMedia(
            { files: [file] },
            pct =>
                toast.update(FLOW_TOAST_KIND, 'Posting song', {
                    detail: `Uploading ${Math.min(99, Math.round(pct))}%`,
                }),
            undefined,
            { uploadKind: 'file', source: 'newMusicFlow' },
        )
        signedId = media.files?.[0]
    } catch (error) {
        console.error('New Music upload failed:', error)
    }
    if (!signedId) {
        toast.update(FLOW_TOAST_KIND, 'Upload failed. Please try again.', {
            variant: 'error',
            detail: '',
        })
        return
    }

    toast.update(FLOW_TOAST_KIND, 'Posting song', { detail: 'Creating thread…' })

    const now = new Date()
    const quest: any = questModel.newQuestFromFilter({}, { public: true })
    quest.created_at = now
    quest.updated_at = now
    quest.parent = {
        id: uuid(),
        content: description,
        delta_json: { ops: [{ insert: `${description}\n` }] },
        files: [signedId],
        ...(form.cover ? { answer_image: form.cover } : {}),
        is_music: true,
        music_title: title,
        created_at: now,
        updated_at: now,
    }

    try {
        const created = await api.createQuest(quest)
        const questId = created?.id || quest.id
        toast.update(FLOW_TOAST_KIND, 'Song posted', { variant: 'success', detail: '' })
        openCenter({ questId: String(questId) })
    } catch (error) {
        console.error('New Music post failed:', error)
        toast.update(FLOW_TOAST_KIND, 'Could not post the song. Please try again.', {
            variant: 'error',
            detail: api.getReadableApiErrorMessage?.(error, '') || '',
        })
    }
}

// One flow at a time: repeated taps while the picker or form is open, or the
// upload is running, must not post the song twice.
export const runNewMusicFlow = createMintMediaThreadRunner(performNewMusicFlow)
